import { useEffect, useRef, useState } from "react";
import * as QRCode from "qrcode";
import { Flex, Loader, Paper, Stack } from "@mantine/core";
import { Title } from "@mantine/core";
import { Text } from "@mantine/core";
import { useOrderComment } from "@jasper/use-cases";
import { joinSocketRoom, onSocketEvent, orderFileRoom } from "@jasper/shared";
import { useDebouncedCallback } from "use-debounce";
import { CommentTextAreaWithFiles } from "./components/CommentTextAreaWithFiles";
import { CommentIndicator } from "./components/CommentIndicator";
import { useAuthContext } from "/@/shared/contexts/AuthContext.tsx";

interface Props {
  orderId: string;
}

export const OrderComment = ({ orderId }: Props) => {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const { user } = useAuthContext();

  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [uploadingError, setUploadingError] = useState<string>();
  const [isUpdatingComment, setIsUpdatingComment] = useState(false);
  const [updatingCommentError, setUpdatingCommentError] = useState<string>();

  const {
    activeOrderComment,
    previousOrderComments,
    isLoading,
    uploadActiveOrderCommentAttachment,
    upsertActiveOrderComment,
    removeActiveOrderCommentAttachment,
    refetchOrder,
  } = useOrderComment({ orderId });

  useEffect(() => {
    const leaveRoom = joinSocketRoom(orderFileRoom(orderId));
    const unsubscribe = onSocketEvent("clientOrderFile", () => {
      void refetchOrder();
    });

    return () => {
      unsubscribe();
      leaveRoom();
    };
  }, [orderId]);

  useEffect(() => {
    const canvasElement = canvas.current;
    if (!canvasElement) {
      return;
    }

    const qrCodeUploadLink = `${window.location.origin}/#/orders/${orderId}/remote-upload?token=${user?.accessToken}`;
    void QRCode.toCanvas(canvasElement, encodeURI(qrCodeUploadLink));
  }, [orderId, user?.accessToken]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100%" w="100%">
        <Loader />
      </Flex>
    );
  }

  const handleFilesAdded = (files: File[]) => {
    setIsUploadingFiles(true);
    setUploadingError(undefined);
    Promise.allSettled(
      files.map(file => uploadActiveOrderCommentAttachment(file))
    )
      .then(results => {
        const rejectedResults = results.filter(
          result => result.status === "rejected"
        ) as PromiseRejectedResult[];
        if (rejectedResults.length > 0) {
          setUploadingError(
            rejectedResults
              .map(result => result.reason?.message ?? "Unknown error")
              .join(", ")
          );
        }
      })
      .finally(() => setIsUploadingFiles(false));
  };

  const handleCommentChange = useDebouncedCallback((value: string) => {
    setIsUpdatingComment(true);
    setUpdatingCommentError(undefined);
    upsertActiveOrderComment(value)
      .catch(error => {
        setUpdatingCommentError(error?.message ?? "Unknown error");
      })
      .finally(() => setIsUpdatingComment(false));
  }, 300);

  const handleRemoveFiles = (files: File[]) => {
    setIsUploadingFiles(true);
    setUploadingError(undefined);

    const removedFileNames = files.map(file => file.name);
    const removedFileIds = activeOrderComment.files
      .filter(f => removedFileNames.includes(f.filename))
      .map(f => f.id);

    Promise.allSettled(
      removedFileIds.map(fileId => removeActiveOrderCommentAttachment(fileId))
    )
      .then(results => {
        const rejectedResults = results.filter(
          result => result.status === "rejected"
        ) as PromiseRejectedResult[];
        if (rejectedResults.length > 0) {
          setUploadingError(
            rejectedResults
              .map(result => result.reason?.message ?? "Unknown error")
              .join(", ")
          );
        }
      })
      .finally(() => setIsUploadingFiles(false));
  };

  return (
    <Paper shadow="xs" p="xl" withBorder>
      <Stack>
        <Title c="primary" order={5}>
          Commentaire
        </Title>
        <CommentTextAreaWithFiles
          orderComment={activeOrderComment}
          onFilesAdded={handleFilesAdded}
          onTextChange={handleCommentChange}
          onFilesRemoved={handleRemoveFiles}
          bottomLeftSection={
            <CommentIndicator
              isUpdatingComment={isUpdatingComment}
              isUploadingFiles={isUploadingFiles}
              updatingCommentError={updatingCommentError}
              uploadingError={uploadingError}
              activeOrderComment={activeOrderComment}
            />
          }
        />
        <Text c="gray" fw={750} tt="uppercase" size="sm">
          Scannez ce QR code pour ajouter un fichier depuis votre téléphone
        </Text>
        <canvas
          style={{
            maxWidth: "170px",
            maxHeight: "170px",
            margin: "auto",
          }}
          ref={canvas}
        />

        {previousOrderComments.length > 0 && (
          <Stack>
            <Title c="primary" order={5}>
              Commentaires précédents
            </Title>
            <Stack>
              {previousOrderComments.map(comment => (
                <CommentTextAreaWithFiles
                  orderComment={comment}
                  disabled
                  key={comment.id}
                />
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
